import { AfterViewInit, Component, ElementRef, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormService } from "../../services/form.service";
import { ApiService } from "../../services/api.service";
import { TrackingService } from "../../services/tracking.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormField } from "@angular/material/form-field";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { betterPhoneNumberValidator } from "../../services/custom-validators";
import { completePhoneNumber } from "../../services/phone-number";

declare var intlTelInput: any;

@Component( {
	selector: 'app-change-phone-modal',
	templateUrl: './change-phone-modal.component.html',
	styleUrls: ['./change-phone-modal.component.scss']
} )
export class ChangePhoneModalComponent implements OnInit, AfterViewInit, OnDestroy {

	public phoneForm: UntypedFormGroup;

	@ViewChild( 'intlInputEl' ) intlInputEl!: ElementRef<HTMLInputElement>;
	@ViewChild( 'phoneField' ) matFormField!: MatFormField;
	private intlInstance: any;
	private numberValidatorAdded = false;
	private validatorState = { isValidating: false };
	public saveLoading = false;
	public saveError: string | null = null;
	public resubmitTick = 0;
	private readonly resubmitCooldownSeconds = 15;
	private resubmitCooldownInterval: ReturnType<typeof setInterval> | null = null;

	constructor(
		public formService: FormService,
		private apiService: ApiService,
		private trackingService: TrackingService,
		private dialogRef: MatDialogRef<ChangePhoneModalComponent>,
		@Inject( MAT_DIALOG_DATA ) public data: { currentPhone: string }
	) {

		this.phoneForm = new UntypedFormGroup( {
			phoneNumber: new UntypedFormControl( data.currentPhone || '', [Validators.required] ),
			phoneNumberComplete: new UntypedFormControl( '' )
		} );
	}

	ngOnInit(): void {

		this.resubmitCooldownInterval = setInterval( () => {

			this.resubmitTick = Date.now();
		}, 1000 );
	}

	ngAfterViewInit(): void {

		setTimeout( () => this.loadIntlTelInput(), 0 );
	}

	ngOnDestroy(): void {

		if( this.resubmitCooldownInterval != null ) {

			clearInterval( this.resubmitCooldownInterval );
		}

		if( this.intlInstance && typeof this.intlInstance.destroy === 'function' ) {

			this.intlInstance.destroy();
		}
	}

	get resubmitCooldownRemaining(): number {

		if( this.formService.lastResubmitPhoneTime == null ) {

			return 0;
		}

		const elapsed = Math.floor( ( Date.now() - this.formService.lastResubmitPhoneTime ) / 1000 );

		return elapsed >= this.resubmitCooldownSeconds ? 0 : this.resubmitCooldownSeconds - elapsed;
	}

	get resubmitAllowed(): boolean {

		return this.resubmitCooldownRemaining === 0 && !this.saveLoading;
	}

	private loadIntlTelInput(): void {

		if( !this.intlInputEl?.nativeElement || typeof intlTelInput !== 'function' ) {

			return;
		}

		const movingFrom = this.formService.mainForm?.get( 'movingFrom' );
		const movingTo = this.formService.mainForm?.get( 'movingTo' );
		const rawFrom = movingFrom?.get( 'country' )?.value;
		const rawTo = movingTo?.get( 'country' )?.value;
		const countryFrom = ( rawFrom != null && rawFrom !== '' ? this.formService.verifyCountryCode( rawFrom ) : null ) ?? 'gb';
		const countryTo = ( rawTo != null && rawTo !== '' ? this.formService.verifyCountryCode( rawTo ) : null ) ?? 'gb';

		const initial = String( countryFrom ).toLowerCase();
		const preferred = [countryFrom, countryTo]
			.filter( c => c != null && c !== '' )
			.map( c => String( c ).toLowerCase() );

		if( preferred.length === 0 ) {

			preferred.push( 'gb' );
		}

		this.intlInstance = intlTelInput( this.intlInputEl.nativeElement, {
			initialCountry: initial,
			preferredCountries: preferred,
			utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.16/js/utils.min.js',
		} );

		if( this.data.currentPhone ) {

			this.intlInstance.setNumber( this.data.currentPhone );
		}

		this.intlInputEl.nativeElement.addEventListener( 'countrychange', () => {

			if( this.validatorState.isValidating ) {
				return;
			}

			this.phoneForm.get( 'phoneNumber' )?.updateValueAndValidity();
		} );

		const runUpdateOutlineGap = () => this.matFormField?.updateOutlineGap();

		setTimeout( () => requestAnimationFrame( runUpdateOutlineGap ), 0 );
		setTimeout( () => requestAnimationFrame( runUpdateOutlineGap ), 150 );
	}

	verifyPhoneNumber(): void {

		if( !this.numberValidatorAdded ) {

			const phoneNumberControl = this.phoneForm.get( 'phoneNumber' );

			phoneNumberControl?.addValidators( betterPhoneNumberValidator( () => this.intlInstance, this.validatorState ) );
			phoneNumberControl?.updateValueAndValidity();
			this.numberValidatorAdded = true;
		}
	}

	onPhoneNumberInput( event: Event ): void {

		const input = event.target as HTMLInputElement;

		input.value = input.value.replace( /[^0-9\s\-()+]/g, '' );
	}

	private formatCompleteNumber(): void {

		const phoneNumber = this.phoneForm.get( 'phoneNumber' )?.value;

		if( phoneNumber && this.intlInstance && typeof this.intlInstance.setNumber === 'function' ) {

			this.intlInstance.setNumber( phoneNumber );
		}

		const number = completePhoneNumber( this.intlInstance, phoneNumber );

		this.phoneForm.get( 'phoneNumberComplete' )?.setValue( number );
	}

	save(): void {

		this.formatCompleteNumber();
		this.saveError = null;

		if( !this.resubmitAllowed ) {

			return;
		}

		if( this.phoneForm.invalid ) {

			Object.keys( this.phoneForm.controls ).forEach( key => {

				this.phoneForm.controls[key].markAsDirty();
				this.phoneForm.controls[key].markAsTouched();
			} );
			return;
		}

		const token = this.formService.token;
		const phoneComplete = this.phoneForm.get( 'phoneNumberComplete' )?.value;

		if( !token ) {

			this.saveError = 'Unable to update phone. Please try again.';
			return;
		}

		this.formService.trackEventWithContext( 'Change phone - submit', 'verification' );
		this.trackingService.clarityEvent( 'otp_phone_updated' );
		this.saveLoading = true;
		this.formService.lastResubmitPhoneTime = Date.now();

		this.apiService.postResubmitPhoneForOtp( token, phoneComplete ).subscribe( {
			next: () => {

				this.saveLoading = false;
				this.dialogRef.close( {
					phoneNumber: this.phoneForm.get( 'phoneNumber' )?.value,
					phoneNumberComplete: phoneComplete
				} );
			},
			error: () => {

				this.saveLoading = false;
				this.saveError = 'Failed to update phone. Please try again.';
			}
		} );
	}

	cancel(): void {

		this.dialogRef.close( null );
	}
}
